/**
 * CustomizeDrawer.jsx — Slide-in style panel
 * Controls: accent color, font family, font scale, spacing
 */
import React, { useState, useEffect, useRef } from 'react';
import { X, RotateCcw, Palette, Type, Baseline, ChevronDown } from 'lucide-react';
import ColorPickerDialog from './ColorPickerDialog';
import { generateThemeColorsGrid } from './colorUtils';
import { THEME_FONTS, RECENT_FONTS, ALL_FONTS } from './fontList';

const STANDARD_COLORS = [
  '#C00000', '#FF0000', '#FFC000', '#FFFF00', '#92D050', '#00B050', '#00B0F0', '#0070C0', '#002060', '#7030A0'
];

export default function CustomizeDrawer({
  open,
  onClose,
  accentColor, setAccentColor,
  fontFamily,  setFontFamily,
  fontScale,   setFontScale,
  theme,
}) {
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFontPickerOpen, setIsFontPickerOpen] = useState(false);
  const [isSizePickerOpen, setIsSizePickerOpen] = useState(false);

  const popoverRef = useRef(null);
  const fontRef = useRef(null);
  const sizeRef = useRef(null);
  const themeGrid = generateThemeColorsGrid();

  useEffect(() => {
    function handleClickOutside(event) {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsPaletteOpen(false);
      }
      if (fontRef.current && !fontRef.current.contains(event.target)) {
        setIsFontPickerOpen(false);
      }
      if (sizeRef.current && !sizeRef.current.contains(event.target)) {
        setIsSizePickerOpen(false);
      }
    }
    if (isPaletteOpen || isFontPickerOpen || isSizePickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPaletteOpen, isFontPickerOpen, isSizePickerOpen]);

  const handleReset = () => {
    setAccentColor('#000000');
    setFontFamily('Inter');
    setFontScale(1);
  };

  return (
    <>
      {/* Overlay */}
      {open && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 199,
            background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(2px)',
          }}
        />
      )}

      {/* Drawer */}
      <aside className={`customize-drawer${open ? ' open' : ''}`} style={{ fontFamily: "'Inter', sans-serif" }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
              Customize
            </h3>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>
              Visual style settings
            </p>
          </div>
          <button
            className="btn-secondary"
            onClick={onClose}
            style={{ padding: 7, borderRadius: 8, minWidth: 'unset' }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Accent Color */}
        <section style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <Palette size={13} color="var(--text-muted)" />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
              Accent Color
            </span>
          </div>

          <div style={{ position: 'relative' }} ref={popoverRef}>
            <button
              onClick={() => setIsPaletteOpen(!isPaletteOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                background: 'var(--card-bg)',
                border: '1px solid var(--card-border)',
                cursor: 'pointer',
                fontSize: 12,
                color: 'var(--text-primary)',
                outline: 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 16,
                  height: 16,
                  borderRadius: 4,
                  background: accentColor,
                  border: '1px solid var(--card-border)'
                }} />
                <span style={{ fontFamily: 'monospace' }}>{accentColor.toUpperCase()}</span>
              </div>
              <ChevronDown size={14} color="var(--text-muted)" />
            </button>

            {/* Office-style Popover */}
            {isPaletteOpen && (
              <div className="office-color-picker-popover">
                {/* Automatic Color Option */}
                <button
                  className="office-popover-item"
                  onClick={() => {
                    setAccentColor('#000000');
                    setIsPaletteOpen(false);
                  }}
                >
                  <div style={{ width: 14, height: 14, background: '#000000', border: '1px solid #ddd' }} />
                  <span><u>A</u>utomatic</span>
                </button>

                <div className="office-divider" />

                {/* Theme Colors Grid */}
                <div className="office-section-header">Theme Colors</div>
                <div className="office-colors-grid">
                  {themeGrid.map((row, rIdx) => 
                    row.map((color, cIdx) => (
                      <button
                        key={`theme-${rIdx}-${cIdx}`}
                        className={`office-color-swatch ${accentColor.toUpperCase() === color.toUpperCase() ? 'selected' : ''}`}
                        style={{ backgroundColor: color }}
                        title={color}
                        onClick={() => {
                          setAccentColor(color);
                          setIsPaletteOpen(false);
                        }}
                      />
                    ))
                  )}
                </div>

                <div className="office-divider" />

                {/* Standard Colors Row */}
                <div className="office-section-header">Standard Colors</div>
                <div className="office-colors-row">
                  {STANDARD_COLORS.map((color, idx) => (
                    <button
                      key={`standard-${idx}`}
                      className={`office-color-swatch ${accentColor.toUpperCase() === color.toUpperCase() ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      title={color}
                      onClick={() => {
                        setAccentColor(color);
                        setIsPaletteOpen(false);
                      }}
                    />
                  ))}
                </div>

                <div className="office-divider" />

                {/* More Colors Button */}
                <button
                  className="office-popover-item"
                  onClick={() => {
                    setIsDialogOpen(true);
                    setIsPaletteOpen(false);
                  }}
                  style={{ fontWeight: 500 }}
                >
                  <Palette size={13} color="#0078D4" />
                  <span><u>M</u>ore Colors...</span>
                </button>
              </div>
            )}
          </div>
        </section>

        <div className="section-divider" />

        {/* Font Family */}
        <section style={{ margin: '18px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <Type size={13} color="var(--text-muted)" />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
              Font Family
            </span>
          </div>

          <div style={{ position: 'relative' }} ref={fontRef}>
            <button
              onClick={() => setIsFontPickerOpen(!isFontPickerOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                background: 'var(--card-bg)',
                border: '1px solid var(--card-border)',
                cursor: 'pointer',
                fontSize: 12,
                color: 'var(--text-primary)',
                outline: 'none',
              }}
            >
              <span style={{ fontFamily: `'${fontFamily}', sans-serif` }}>{fontFamily}</span>
              <ChevronDown size={14} color="var(--text-muted)" />
            </button>

            {/* Word-style Font popover */}
            {isFontPickerOpen && (
              <div className="word-font-picker-popover">
                {/* Theme Fonts Section */}
                <div className="word-font-section-header">Theme Fonts</div>
                {THEME_FONTS.map(f => (
                  <button
                    key={`theme-font-${f.value}`}
                    className={`word-font-row ${fontFamily === f.value ? 'selected' : ''}`}
                    onClick={() => {
                      setFontFamily(f.value);
                      setIsFontPickerOpen(false);
                    }}
                    style={{ fontFamily: `'${f.value}', sans-serif` }}
                  >
                    <span>{f.label}</span>
                    <span className="word-font-meta">({f.type})</span>
                  </button>
                ))}

                {/* Recently Used Fonts Section */}
                <div className="word-font-section-header">Recently Used Fonts</div>
                {RECENT_FONTS.map(f => (
                  <button
                    key={`recent-font-${f.value}`}
                    className={`word-font-row ${fontFamily === f.value ? 'selected' : ''}`}
                    onClick={() => {
                      setFontFamily(f.value);
                      setIsFontPickerOpen(false);
                    }}
                    style={{ fontFamily: `'${f.value}', sans-serif` }}
                  >
                    {f.label}
                  </button>
                ))}

                {/* All Fonts Section */}
                <div className="word-font-section-header">All Fonts</div>
                {ALL_FONTS.map(fontName => (
                  <button
                    key={`all-font-${fontName}`}
                    className={`word-font-row ${fontFamily === fontName ? 'selected' : ''}`}
                    onClick={() => {
                      setFontFamily(fontName);
                      setIsFontPickerOpen(false);
                    }}
                    style={{ fontFamily: `'${fontName}', sans-serif` }}
                  >
                    {fontName}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div style={{
            marginTop: 8, padding: '8px 10px', borderRadius: 8,
            background: 'var(--card-bg)', border: '1px solid var(--card-border)',
            fontFamily: `'${fontFamily}', sans-serif`,
            fontSize: 13, color: 'var(--text-secondary)',
          }}>
            Aa Bb Cc — {fontFamily}
          </div>
        </section>

        <div className="section-divider" />

        {/* Font Size */}
        <section style={{ margin: '18px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <Baseline size={13} color="var(--text-muted)" />
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
              Font Size
            </span>
          </div>

          <div style={{ position: 'relative' }} ref={sizeRef}>
            <button
              onClick={() => setIsSizePickerOpen(!isSizePickerOpen)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                background: 'var(--card-bg)',
                border: '1px solid var(--card-border)',
                cursor: 'pointer',
                fontSize: 12,
                color: 'var(--text-primary)',
                outline: 'none',
              }}
            >
              <span>{Math.round(fontScale * 9)}</span>
              <ChevronDown size={14} color="var(--text-muted)" />
            </button>

            {/* Word-style Font Size popover */}
            {isSizePickerOpen && (
              <div className="word-size-picker-popover">
                {[8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72].map(sz => {
                  const isSelected = Math.round(fontScale * 9) === sz;
                  return (
                    <button
                      key={`font-size-${sz}`}
                      className={`word-size-row ${isSelected ? 'selected' : ''}`}
                      onClick={() => {
                        setFontScale(sz / 9);
                        setIsSizePickerOpen(false);
                      }}
                    >
                      {sz}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <div className="section-divider" />

        {/* Reset */}
        <button
          className="btn-secondary"
          onClick={handleReset}
          style={{ width: '100%', marginTop: 18, fontSize: 12, justifyContent: 'center' }}
        >
          <RotateCcw size={13} />
          Reset to Defaults
        </button>

        {/* Windows Color Picker Dialog */}
        <ColorPickerDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          currentColor={accentColor}
          onSelectColor={(color) => setAccentColor(color)}
        />
      </aside>
    </>
  );
}
